import {
  TeCell,
  TeContainerSlim,
  TeDropdown,
  TeDropdownOption,
  TeIcon,
  TeLoader,
  TePagination,
  TeRow,
  TeTable,
  TeText,
  TeTooltip,
} from '@topcon/tuicl-react';
import { TePageChangeEvent } from '@topcon/tuicl';
import { useTranslation } from 'react-i18next';
import { ReactNode } from 'react';
import styled from 'styled-components';

import EmptyState from '../../../components/ServiceAgreement/Common/EmptyState';
import { DEFAULT_LIST_PAGE_SIZE, LIST_PAGE_SIZES, ListPageSize } from '../../../hooks/useListState';

export interface ServerPagedTableColumn<T> {
  key: string;
  header: ReactNode;
  /** Renders a ⓘ next to the header with this text (Organization no. /
   * Customer number, PRD 02 "Table"). */
  headerInfoTooltip?: string;
  /** Compact (non-growing) cell, for icon-only or ⋮ menu columns. */
  compact?: boolean;
  /** Lets a long-text column (name, Division, address, country) shrink down
   * to this many px, its `CellText` ending in an ellipsis, instead of
   * widening the table. Columns without it keep their content's single-line
   * width (numbers, dates, badges). */
  minWidth?: number;
  /** `flex-end` for numeric and date columns (Figma right-aligns them). */
  justifyContent?: 'flex-start' | 'center' | 'flex-end';
  render: (item: T) => ReactNode;
}

export interface ServerPagedTableProps<T> {
  columns: ServerPagedTableColumn<T>[];
  items: T[];
  rowKey: (item: T) => string;
  total: number;
  page: number;
  limit: ListPageSize;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: ListPageSize) => void;
  /** True only while the first page of results is in flight; once data has
   * loaded once, pass `placeholderData` (see `customers.queries.ts`) so this
   * stays `false` across later page/filter changes (no table flash). */
  isLoading: boolean;
  /** True while a later page/filter change is in flight. Dims the table
   * instead of replacing it with the loading placeholder. */
  isFetching?: boolean;
  /** True when the list query failed and the error drawer did not take it
   * (`showError` returned `false`). Renders the shared error placeholder in
   * place of the rows; there is no retry control, recovery is the page's
   * `useVisibilityRefetch` or any page/filter/search change. */
  isError?: boolean;
  /** Rendered instead of the table when `total` is 0 and loading has
   * finished. The caller chooses `noItems` vs `noSearch` `EmptyState`. */
  emptyState: ReactNode;
}

/**
 * Server-paged, full-width table scaffold shared by Customers, Invites and
 * Requests (PRD D10, D11): column header row, rows, loading and error
 * placeholders, an empty slot, and a footer with a page-size select
 * (20/50/100) and `TePagination`. No sortable headers (PRD D2).
 */
export default function ServerPagedTable<T>({
  columns,
  items,
  rowKey,
  total,
  page,
  limit,
  onPageChange,
  onLimitChange,
  isLoading,
  isFetching = false,
  isError = false,
  emptyState,
}: ServerPagedTableProps<T>) {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <TeTable evenOdd nested staticHeader>
        <HeaderRow columns={columns} />
        <TeRow>
          <TeCell justifyContent="center" colspan={columns.length}>
            <TeContainerSlim gap="xs" alignItems="center" justifyContent="center" padding="l">
              <TeLoader size="s" />
              <TeText color="text.secondary">{t('loading', { ns: 'common' })}</TeText>
            </TeContainerSlim>
          </TeCell>
        </TeRow>
      </TeTable>
    );
  }

  if (isError) {
    return <EmptyState kind="error" resource="customers" />;
  }

  if (total === 0) {
    return <>{emptyState}</>;
  }

  const pages = Math.max(1, Math.ceil(total / limit));

  return (
    // `overflow="hidden"` lets this column shrink below its content so the
    // table body scrolls under its sticky header and the footer stays visible.
    <TeContainerSlim column fill grow overflow="hidden">
      <TeTable
        evenOdd
        nested
        staticHeader
        style={{ opacity: isFetching ? 0.75 : 1, pointerEvents: isFetching ? 'none' : 'auto' }}
      >
        <HeaderRow columns={columns} />
        {items.map((item) => (
          <TeRow key={rowKey(item)}>
            {columns.map((column) => (
              <TeCell
                key={column.key}
                compact={column.compact}
                minWidth={column.minWidth}
                justifyContent={column.justifyContent}
              >
                {column.minWidth === undefined ? (
                  column.render(item)
                ) : (
                  <ShrinkToColumn>{column.render(item)}</ShrinkToColumn>
                )}
              </TeCell>
            ))}
          </TeRow>
        ))}
      </TeTable>
      <TeContainerSlim
        justifyContent="space-between"
        alignItems="center"
        padding={['xs', 'm']}
        backgroundColor="background.primary"
        width="100%"
      >
        <TeDropdown
          size="s"
          style={{ width: 70 }}
          valueByDefault={String(DEFAULT_LIST_PAGE_SIZE)}
          value={String(limit)}
          persistent
          disabled={isFetching}
        >
          {LIST_PAGE_SIZES.map((size) => (
            <TeDropdownOption
              key={size}
              text={String(size)}
              value={String(size)}
              teclick={() => onLimitChange(size)}
            />
          ))}
        </TeDropdown>
        <TePagination
          size="s"
          pages={pages}
          page={page}
          blockInteraction={isFetching}
          techange={(e: TePageChangeEvent) => onPageChange(e.page)}
        />
      </TeContainerSlim>
    </TeContainerSlim>
  );
}

/**
 * Zero intrinsic width, then stretched to the cell: the column's width comes
 * from its header and `minWidth` rather than from the (truncating) content,
 * so long text can't push the table wider than the page.
 */
const ShrinkToColumn = styled.div`
  width: 0;
  min-width: 100%;
`;

function HeaderRow<T>({ columns }: { columns: ServerPagedTableColumn<T>[] }) {
  return (
    <TeRow>
      {columns.map((column) => (
        <TeCell
          key={column.key}
          compact={column.compact}
          minWidth={column.minWidth}
          justifyContent={column.justifyContent}
        >
          <TeContainerSlim gap="xs2" alignItems="center">
            <TeText weight={600} size={14} color="text.primary" truncate>
              {column.header}
            </TeText>
            {column.headerInfoTooltip && (
              <>
                <TeIcon
                  name="info"
                  size="s"
                  color="icon.primary"
                  popkey={`server-paged-table-header-${column.key}`}
                />
                <TeTooltip
                  for={`server-paged-table-header-${column.key}`}
                  text={column.headerInfoTooltip}
                />
              </>
            )}
          </TeContainerSlim>
        </TeCell>
      ))}
    </TeRow>
  );
}
